import React, { useState, useMemo } from "react";
import { accountData } from "./data";

const AccountPlan = () => {
  // Default to ID 100 (Vanguard) since our new data starts at 100
  const [selectedId, setSelectedId] = useState(100);

  const selectedAccount =
    accountData.find((a) => a.id === selectedId) || accountData[0];

  // Grouping logic for the sidebar
  const groupedAccounts = useMemo(() => {
    const groups = {};
    const namerAccounts = accountData.filter((a) => a.region === "NAMER");

    namerAccounts.forEach((acc) => {
      if (!groups[acc.vertical]) groups[acc.vertical] = {};
      if (!groups[acc.vertical][acc.tier]) groups[acc.vertical][acc.tier] = [];
      groups[acc.vertical][acc.tier].push(acc);
    });
    return groups;
  }, []);

  return (
    <div className="ap-grid">
      {/* Sidebar Navigation */}
      <aside className="ap-nav">
        <h3
          style={{
            fontSize: "14px",
            fontWeight: 700,
            marginBottom: "16px",
            color: "#0f172a",
          }}
        >
          NAMER Accounts
        </h3>

        {Object.keys(groupedAccounts).map((vertical) => (
          <div key={vertical} className="ap-section">
            <div className="ap-sec-title">{vertical}</div>
            {Object.keys(groupedAccounts[vertical]).map((tier) => (
              <div key={tier} style={{ marginBottom: "12px" }}>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#94a3b8",
                    paddingLeft: "8px",
                    marginBottom: "4px",
                  }}
                >
                  {tier}
                </div>
                <ul className="ap-sublist">
                  {groupedAccounts[vertical][tier].map((acc) => (
                    <li key={acc.id}>
                      <button
                        className={`ap-link ${
                          selectedId === acc.id ? "active" : ""
                        }`}
                        onClick={() => setSelectedId(acc.id)}
                      >
                        {acc.name}
                        {selectedId === acc.id && <div className="ap-dot" />}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))}
      </aside>

      {/* Main Content Area */}
      <main className="ap-editor">
        <div className="ap-header">
          <div>
            <div className="ap-breadcrumb">
              {selectedAccount.region} / {selectedAccount.vertical} /{" "}
              {selectedAccount.tier}
            </div>
            <h2 className="ap-title">{selectedAccount.name}</h2>
          </div>
          <div className="ap-meta">
            <span
              className="badge"
              style={{
                background: "#eff6ff",
                color: "#1d4ed8",
                border: "none",
              }}
            >
              Owner: {selectedAccount.owner}
            </span>
            <span className="badge" style={{ marginLeft: "8px" }}>
              Propensity: {selectedAccount.propensity}
            </span>
          </div>
        </div>

        {/* Account Plan Details Form */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px",
            marginTop: "20px",
          }}
        >
          <div
            className="card"
            style={{
              padding: "16px",
              boxShadow: "none",
              background: "#f8fafc",
            }}
          >
            <h4 style={{ margin: "0 0 12px 0", fontSize: "14px" }}>
              2026 Strategy
            </h4>

            <div className="ap-field-group">
              <label className="ap-label">Primary Business Objective</label>
              <textarea
                className="ap-textarea"
                style={{ minHeight: "80px" }}
                key={`strat-${selectedAccount.id}`}
                defaultValue={
                  selectedAccount.strategy ||
                  "Drive enterprise-wide upskilling in GenAI and Data Science."
                }
              />
            </div>

            <div className="ap-field-group" style={{ marginTop: "12px" }}>
              <label className="ap-label">Key Stakeholders to Engage</label>
              <textarea
                className="ap-textarea"
                style={{ minHeight: "60px" }}
                key={`stake-${selectedAccount.id}`}
                defaultValue={
                  selectedAccount.stakeholders || "CHRO, CTO, Head of L&D"
                }
              />
            </div>
          </div>

          <div
            className="card"
            style={{
              padding: "16px",
              boxShadow: "none",
              background: "#f8fafc",
            }}
          >
            <h4 style={{ margin: "0 0 12px 0", fontSize: "14px" }}>
              Opportunity Data
            </h4>

            <div className="row gap" style={{ marginBottom: "12px" }}>
              <div style={{ flex: 1 }}>
                <label className="ap-label">Est. Revenue</label>
                <div style={{ fontSize: "16px", fontWeight: 600 }}>
                  {selectedAccount.revenue}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <label className="ap-label">Renewal Date</label>
                <div style={{ fontSize: "16px", fontWeight: 600 }}>
                  {selectedAccount.renewal}
                </div>
              </div>
            </div>

            <div className="ap-field-group">
              <label className="ap-label">Current blockers / Notes</label>
              <textarea
                className="ap-textarea"
                style={{ minHeight: "80px" }}
                key={`block-${selectedAccount.id}`}
                defaultValue={
                  selectedAccount.blockers ||
                  "Budget freeze in Q1; exploring competitive solution."
                }
              />
            </div>
          </div>
        </div>

        <div style={{ marginTop: "20px" }}>
          <h4 style={{ fontSize: "14px", marginBottom: "8px" }}>
            Action Plan Q1 2026
          </h4>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: "15%" }}>Status</th>
                <th>Task</th>
                <th style={{ width: "20%" }}>Owner</th>
                <th style={{ width: "20%" }}>Due</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <span
                    className="badge"
                    style={{
                      background: "#dcfce7",
                      color: "#166534",
                      border: "none",
                    }}
                  >
                    Done
                  </span>
                </td>
                <td>Executive Briefing preparation</td>
                <td>{selectedAccount.owner}</td>
                <td>Jan 10, 2026</td>
              </tr>
              <tr>
                <td>
                  <span
                    className="badge"
                    style={{
                      background: "#fef9c3",
                      color: "#854d0e",
                      border: "none",
                    }}
                  >
                    In Progress
                  </span>
                </td>
                <td>Custom curriculum mapping</td>
                <td>Solution Consultant</td>
                <td>Feb 15, 2026</td>
              </tr>
              <tr>
                <td>
                  <span className="badge">Pending</span>
                </td>
                <td>QBR & Renewal discussion</td>
                <td>{selectedAccount.owner}</td>
                <td>Mar 01, 2026</td>
              </tr>
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default AccountPlan;
